import {
  Button,
  Box,
  Grid,
  Dialog,
  ProductPrice,
  InlineNotification,
  Chip,
  Typography,
  LinkButton,
  Link,
} from '@packages/shared';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { BuyBoxDataFragmentDoc } from '@packages/gql/generated/shopping/BuyBoxDataFragmentDoc';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { StickyInsideParent, StickyOutsideViewport } from '../Sticky';
import { HintPanel } from '../HintPanel';
import { PremiumBanner } from '../PremiumBanner';
import { BusinessTerms } from '../BusinessTerms';
import { Co2CompensationFlag } from '../Co2CompensationFlag';
import { checkoutPathByPage } from '../../utils/checkoutPages';

/* GraphQL */ `
  fragment BuyBoxData on Basket {
    subTotal {
      amount
      currency
    }
    saving {
      amount
      currency
    }
    freeShipping
    banners {
      type
      articleNumber
      coupon
    }
    targetPage
  }
`;

export interface BuyBoxProps {
  data?: FragmentType<typeof BuyBoxDataFragmentDoc>;
}

export const BuyBox: React.FC<BuyBoxProps> = ({ data }) => {
  const fragmentData = getFragmentData(BuyBoxDataFragmentDoc, data);
  const { locale } = useIntl();
  const { formatPrice } = useFormatPrice();
  const {
    order: { hasCo2Compensation, premium },
  } = useConfig();
  const { isDesktop } = useDeviceType();

  const [shippingCostModalOpen, setShippingCostModalOpen] = useState<boolean>(false);
  const [hasBonusCodes, setHasBonusCodes] = useState<boolean>(false);

  useEffect(() => {
    const bonusCodes = [];
    // TODO for api manager
    // const bonusCodes = apiKeyManager.getBonuscodes();
    setHasBonusCodes(bonusCodes.length > 0);
  }, []);

  if (!fragmentData) {
    return null;
  }

  const { banners, subTotal, saving, targetPage } = fragmentData;
  const ConditionalStickyWrapper = isDesktop ? StickyInsideParent : StickyOutsideViewport;

  const shouldHideCo2Hint =
    isDesktop && banners && banners.find((banner) => banner.type === 'PREMIUM');
  const shouldShowCo2Hint = isDesktop && hasCo2Compensation && !shouldHideCo2Hint;

  return (
    <ConditionalStickyWrapper
      sx={{
        bgcolor: 'common.white',
        boxShadow: {
          xs: '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 1px 10px 0 rgba(0, 0, 0, 0.12),0 4px 5px 0 rgba(0, 0, 0, 0.14)',
          lg: 'none',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          boxSizing: 'border-box',
          borderWidth: {
            xs: '0',
            md: '1px',
          },
          borderStyle: 'solid',
          borderColor: 'grey.main',
          borderRadius: {
            xs: 0,
            md: '3px',
          },
        }}
      >
        <Box
          sx={{
            p: 1.5,
            pb: 2.5,
            bgcolor: 'common.white',
          }}
        >
          <Grid
            container
            columns={{ xs: 2, md: 2, lg: 2 }}
            columnSpacing="4px"
            rowSpacing="6px"
            justifyContent="space-between"
          >
            <Grid item>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: 'text.dark',
                }}
              >
                <FormattedMessage id="BuyBox.SubTotal" defaultMessage="Zwischensumme" />
              </Typography>
            </Grid>
            <Grid item>
              {subTotal && (
                <ProductPrice
                  fontVariantPrice="body1"
                  price={subTotal.amount}
                  currency={subTotal.currency}
                />
              )}
            </Grid>
          </Grid>
          {saving && (
            <Grid
              container
              columns={{ xs: 2, md: 2, lg: 2 }}
              columnSpacing="4px"
              rowSpacing="6px"
              justifyContent="space-between"
              alignItems="center"
              wrap="nowrap"
            >
              <Grid item>
                <Typography
                  variant="body2"
                  color="success.main"
                  sx={{
                    overflowWrap: 'break-word',
                  }}
                >
                  <FormattedMessage
                    id="BuyBox.PriceReduction"
                    defaultMessage="Du sparst bei deinem Einkauf"
                  />
                </Typography>
              </Grid>
              <Grid
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: '0rem 0.5rem',
                  bgcolor: 'success.main',
                  borderRadius: '3px',
                }}
              >
                <Typography variant="body3" color="text.light">
                  {formatPrice(saving.amount, saving.currency satisfies string)}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Grid
            container
            columns={{ xs: 2, md: 2, lg: 1 }}
            columnSpacing="4px"
            rowSpacing="6px"
            justifyContent="flex-start"
            sx={{
              mt: '0.313rem',
            }}
          >
            <Grid item lg={1}>
              <Typography variant="body3" color="text.darkTransparent" pr="0.25rem">
                <FormattedMessage
                  id="BuyBox.LegalHint"
                  defaultMessage="Alle Angaben in Euro inkl. MwSt."
                />
              </Typography>
            </Grid>
            <Grid item lg={1} sx={{ color: 'text.darkTransparent' }}>
              <LinkButton variant="body3" onClick={() => setShippingCostModalOpen(true)}>
                <FormattedMessage
                  id="BuyBox.ShippingCostHint"
                  defaultMessage="zzgl. Versandkosten"
                />
              </LinkButton>
            </Grid>
          </Grid>
          {(saving || hasBonusCodes) && (
            <InlineNotification
              severity="info"
              sx={{
                mt: 2,
                p: '0 0.5rem',
              }}
            >
              <Typography variant="body2">
                <FormattedMessage
                  id="BuyBox.CouponDiscountHint"
                  defaultMessage="Gutschein- und Rabattcodes werden in Schritt 5 zusätzlich abgezogen!"
                />
              </Typography>
            </InlineNotification>
          )}
          <Box
            sx={{
              mt: 1.5,
            }}
          >
            <Button
              LinkComponent={Link}
              color="primary"
              fullWidth
              href={getLocalizedPath(checkoutPathByPage[targetPage], locale)}
            >
              <FormattedMessage id="BuyBox.GoToCheckout" defaultMessage="Zur Kasse" />
            </Button>
          </Box>
          {shouldShowCo2Hint && (
            <Box mt={1.5}>
              <Co2CompensationFlag />
            </Box>
          )}
          {isDesktop && banners && banners.length > 0 && (
            <Box mt={3}>
              {banners.map((banner) => {
                if (banner.type === 'PREMIUM' && premium.enabled) {
                  return (
                    <HintPanel
                      data-testid="premium-banner"
                      key={banner.articleNumber}
                      header={
                        <Chip
                          size="small"
                          color="default"
                          label={
                            <Typography variant="body3" textTransform="uppercase">
                              <FormattedMessage id="BuyBox.HintHeader" defaultMessage="Tipp" />
                            </Typography>
                          }
                        />
                      }
                    >
                      <Box p="1rem 0.5rem">
                        <PremiumBanner
                          articleNumber={banner.articleNumber || undefined}
                          mode="Basket"
                        />
                      </Box>
                    </HintPanel>
                  );
                }
                return null;
              })}
            </Box>
          )}
        </Box>
        <BusinessTerms />
      </Box>
      <Dialog
        title=""
        open={shippingCostModalOpen}
        handleClose={() => setShippingCostModalOpen(false)}
        dialogContent={
          <Box px={2}>
            {/* TODO }
          <EventTracking eventCategory="deliverycosts" isLayer />
          { */}
            <ServiceLayer serviceLayerId="versandkosten_io" />
          </Box>
        }
      />
    </ConditionalStickyWrapper>
  );
};
